import React from 'react';
import { RADIUS, TAP, useTheme } from '../../theme';
import { baseButton, nextIndex } from './shared';

export type SegmentOption<V extends string> = { value: V; label: React.ReactNode; icon?: React.ReactNode; disabled?: boolean };

/**
 * Segmentväljare (radiogrupp). Piltangenter flyttar valet, bara det valda segmentet är i tabbordningen.
 * size md = 44 px tryckyta per segment, sm = 36 px för täta desktopytor.
 */
export function Segmented<V extends string>({ options, value, onChange, label, size = 'md', block = true, style }: {
  options: SegmentOption<V>[]; value: V; onChange: (v: V) => void;
  /** Gruppens namn för skärmläsare. */
  label: string;
  size?: 'sm' | 'md'; block?: boolean; style?: React.CSSProperties;
}) {
  const T = useTheme();
  const refs = React.useRef<(HTMLButtonElement | null)[]>([]);
  const cur = Math.max(0, options.findIndex((o) => o.value === value));
  const onKey = (e: React.KeyboardEvent) => {
    const i = nextIndex(e.key, cur, options.map((o) => !!o.disabled));
    if (i === null) return;
    e.preventDefault();
    onChange(options[i].value);
    refs.current[i]?.focus();
  };
  return (
    <div role="radiogroup" aria-label={label} onKeyDown={onKey} style={{
      display: block ? 'flex' : 'inline-flex', gap: 4, padding: 3, background: T.surface2, borderRadius: RADIUS.md, ...style,
    }}>
      {options.map((o, i) => {
        const on = i === cur;
        return (
          <button
            key={o.value} ref={(el) => { refs.current[i] = el; }} type="button" role="radio" aria-checked={on}
            tabIndex={on ? 0 : -1} disabled={o.disabled} onClick={() => onChange(o.value)}
            style={{
              ...baseButton, flex: block ? 1 : undefined, minHeight: size === 'md' ? TAP : 36, padding: '0 14px',
              borderRadius: RADIUS.md - 3, fontSize: 14, fontWeight: 700,
              background: on ? T.surface : 'transparent', color: on ? T.text : T.textSub,
              boxShadow: on ? T.shadow.card : 'none', opacity: o.disabled ? 0.5 : 1,
              transition: 'background .15s, color .15s',
            }}
          >
            {o.icon}{o.label}
          </button>
        );
      })}
    </div>
  );
}
